import React, {FC, useEffect, useState} from "react";
import {Button, Col, DatePicker, Drawer, Form, InputNumber, Row, Select, Spin} from "antd";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import moment from "moment";

interface QuickLocationAssignmentProps {
    visible: boolean;
    order: any;
    onClose: () => void;
    onSuccess: () => void;
}

const QuickLocationAssignment: FC<QuickLocationAssignmentProps> = ({ visible, order, onClose, onSuccess }) => {
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [locationOptions, setLocationOptions] = useState<any[]>([]);

    useEffect(() => {
        if (visible) {
            getLocations();
            if (order) {
                form.setFieldsValue({
                    itemId: order.item && order.item.id,
                    quantity: order.orderQuantity - order.receivedQuantity,
                    validFrom: moment(),
                    validTo: moment().add(1, 'year')
                });
            }
        }
    }, [visible, order]);

    const getLocations = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/locations')
                .then(response => {
                    const locations = Array.isArray(response.data) ? response.data : [];
                    setLocationOptions(locations);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            if (axiosInstance.current != null) {
                setLoading(true);
                
                const itemId = values.itemId || (order.item && order.item.id);
                
                // Build item object - use id if available, otherwise use itemNo and supplierCode
                let itemPayload: any;
                if (itemId) {
                    itemPayload = { id: itemId };
                } else if (order.itemNo && order.supplierCode) {
                    // If no itemId, use itemNo and supplierCode (from PendingOrderView)
                    itemPayload = {
                        itemNo: order.itemNo,
                        supplier: { code: order.supplierCode }
                    };
                } else {
                    openNotification('error', 'Item information is required. Please ensure the item exists.');
                    setLoading(false);
                    return;
                }
                
                const payload = {
                    location: { id: values.locationId },
                    item: itemPayload,
                    quantity: values.quantity || null,
                    validFrom: values.validFrom ? moment(values.validFrom).format('YYYY-MM-DD HH:mm') : null,
                    validTo: values.validTo ? moment(values.validTo).format('YYYY-MM-DD HH:mm') : null
                };

                axiosInstance.current
                    .post('/resource/location-assignment-rules/from-dto', payload)
                    .then(() => {
                        openNotification('customSuccess', 'Location Assignment Rule created successfully.');
                        form.resetFields();
                        onSuccess();
                        onClose();
                    })
                    .catch(function (error: any) {
                        openNotification('error', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    };

    return (
        <Drawer
            title={<Trans>Quick Location Assignment</Trans>}
            placement="right"
            width={500}
            onClose={onClose}
            visible={visible}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={onClose} style={{ marginRight: 8 }}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button onClick={handleSubmit} type="primary" loading={loading}>
                        <Trans>Create Location Rule</Trans>
                    </Button>
                </div>
            }
        >
            <Spin spinning={loading}>
                <Form form={form} layout="vertical">
                    {order && (
                        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                            <p style={{ margin: 0 }}><strong><Trans>Order No:</Trans></strong> {order.orderNo}</p>
                            <p style={{ margin: 0 }}><strong><Trans>Item:</Trans></strong> {order.itemNo || (order.item && order.item.itemNo)}</p>
                        </div>
                    )}

                    <Form.Item
                        name="locationId"
                        label={<Trans>Location</Trans>}
                        rules={[{ required: true, message: <Trans>Please select a location</Trans> }]}
                    >
                        <Select
                            showSearch
                            allowClear
                            placeholder={<Trans>Select location</Trans>}
                            optionFilterProp="children"
                        >
                            {locationOptions.map(loc => (
                                <Select.Option key={loc.id} value={loc.id}>
                                    {loc.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="quantity"
                        label={<Trans>Quantity</Trans>}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="validFrom"
                                label={<Trans>Valid From</Trans>}
                            >
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="validTo"
                                label={<Trans>Valid To</Trans>}
                            >
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Drawer>
    );
};

export default QuickLocationAssignment;
